/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */
import { TextField, TextFieldProps } from "@mui/material";
import { ChangeEvent, FC, useCallback, useRef, useState } from "react";
export interface ConditionalTextFieldProps {
  label?: string;
  value?: string;
  maxLength?: number;
  acceptPattern?: string;
  textFieldProps?: TextFieldProps;
  onChanged?: (text: string) => void;
}
export const ConditionalTextField: FC<ConditionalTextFieldProps> = ({
  label,
  value,
  maxLength,
  acceptPattern,
  textFieldProps,
  onChanged,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const inComposition = useRef(false);
  const notCompositionValue = useRef<string>();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let str = event.target.value;
      if (!inComposition.current) {
        if (acceptPattern !== undefined) {
          str = str.match(new RegExp(acceptPattern, "g"))?.join("") ?? "";
        }
        if (maxLength !== undefined && str.length > maxLength) {
          str = notCompositionValue.current ?? str.substring(0, maxLength);
        }
      }
      setLocalValue((pre) => {
        if (pre !== str) {
          return str;
        }
        return pre;
      });
      if (!inComposition.current) {
        onChanged?.(str);
      }
    },
    [acceptPattern, maxLength, onChanged]
  );

  const handleCompositionStart = useCallback(() => {
    inComposition.current = true;
    notCompositionValue.current = localValue;
  }, [localValue]);

  const handleCompositionEnd = useCallback(
    (e: unknown) => {
      inComposition.current = false;
      handleChange(e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
      notCompositionValue.current = undefined;
    },
    [handleChange]
  );

  return (
    <TextField
      {...textFieldProps}
      label={label}
      value={localValue}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      inputProps={{
        ...textFieldProps?.inputProps,
        maxLength: maxLength,
      }}
      sx={{
        position: "relative",
        backgroundColor: "white",
      }}
    />
  );
};
